import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-3.5 [&>svg]:h-4 [&>svg]:w-4 [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'border-destructive-border bg-destructive-subtle text-destructive-emphasis [&>svg]:text-destructive-emphasis',
        success:
          'border-success-border bg-success-subtle text-success-emphasis [&>svg]:text-success-emphasis',
        warning:
          'border-warning-border bg-warning-subtle text-warning-emphasis [&>svg]:text-warning-emphasis',
        info: 'border-info-border bg-info-subtle text-info-emphasis [&>svg]:text-info-emphasis',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type AlertProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & {
    /**
     * A non-destructive notice that arrives as the result of the user's action
     * ("Template created"): announced politely with role="status". Static
     * guidance and warnings stay silent. Destructive Alerts are always
     * role="alert" and ignore this.
     */
    announce?: boolean;
  };

/**
 * Live roles live here, not at call sites: destructive → role="alert";
 * every other variant carries no live role unless `announce` (role="status").
 * An explicit `role` still wins (ConsequenceAlert passes role="status").
 */
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, announce = false, role, ...props }, ref) => (
    <div
      ref={ref}
      role={role ?? (variant === 'destructive' ? 'alert' : announce ? 'status' : undefined)}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  ),
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
